import type { AuthResult, Entitlement, Me } from '@dash-lite/sdk';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState  } from 'react';
import type {ReactNode} from 'react';
import { applyToken, dashlite, readStoredToken } from '@/lib/dashlite';
import { isApiError } from '@/lib/errors';

/**
 * Session de l'utilisateur final : token, profil et droit d'accès.
 *
 * Trois idées structurent ce contexte, et elles valent pour toute intégration
 * de l'API Dash Lite :
 *
 * 1. **Il n'y a pas d'inscription.** Un compte naît d'un paiement réussi. La
 *    session démarre donc soit par `POST /auth/login` (client qui revient),
 *    soit par `POST /payment/claim` (client qui vient d'acheter) — les deux
 *    renvoient exactement la même structure `AuthResult`.
 *
 * 2. **`entitlement.has_access` est le seul interrupteur d'accès.** On ne
 *    recalcule jamais l'accès à partir des dates ou du statut : le champ tient
 *    déjà compte du délai de grâce après un échec de paiement et de la fin de
 *    période payée après une résiliation.
 *
 * 3. **Un token refusé est un token à jeter.** Sur `unauthenticated` ou
 *    `environment_mismatch`, on purge le stockage local immédiatement, sans
 *    quoi chaque appel suivant repartirait avec le même token mort.
 */

type SessionContextValue = {
    /** Profil courant, ou `null` si personne n'est connecté. */
    user: Me | null;
    /** Droit d'accès courant, rafraîchi indépendamment du profil. */
    entitlement: Entitlement | null;
    /** `true` une fois le token du stockage local vérifié au démarrage. */
    ready: boolean;
    isAuthenticated: boolean;
    /** Le raccourci consommé par les pages : ouvrir ou non le contenu. */
    hasAccess: boolean;
    login: (payload: { email: string; password: string }) => Promise<void>;
    logout: () => Promise<void>;
    /** Adopte le résultat d'un login OU d'un claim de paiement. */
    adoptAuthResult: (auth: AuthResult) => void;
    /** Relit `GET /me` (profil + droit d'accès). */
    refreshUser: () => Promise<void>;
    /** Relit `GET /entitlement` seul — moins de données, plus fréquent. */
    refreshEntitlement: () => Promise<Entitlement | null>;
    /** Suppression de compte (RGPD) : déconnecte et purge la session. */
    deleteAccount: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<Me | null>(null);
    const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
    const [ready, setReady] = useState(false);

    /** Efface toute trace de session, côté client comme côté stockage. */
    const clearSession = useCallback(() => {
        applyToken(null);
        setUser(null);
        setEntitlement(null);
    }, []);

    /**
     * Traite une erreur d'appel authentifié.
     *
     * Retourne `true` si la session a été invalidée — l'appelant sait alors
     * qu'il n'a plus rien à réessayer.
     */
    const handleAuthFailure = useCallback(
        (error: unknown): boolean => {
            if (isApiError(error, 'unauthenticated', 'environment_mismatch')) {
                clearSession();

                return true;
            }

            return false;
        },
        [clearSession],
    );

    const refreshUser = useCallback(async () => {
        if (!dashlite.getToken()) {
            return;
        }

        try {
            const me = await dashlite.me();
            setUser(me);
            setEntitlement(me.entitlement);
        } catch (error) {
            if (!handleAuthFailure(error)) {
                throw error;
            }
        }
    }, [handleAuthFailure]);

    const refreshEntitlement = useCallback(async (): Promise<Entitlement | null> => {
        if (!dashlite.getToken()) {
            return null;
        }

        try {
            const fresh = await dashlite.getEntitlement();
            setEntitlement(fresh);

            return fresh;
        } catch (error) {
            if (handleAuthFailure(error)) {
                return null;
            }

            throw error;
        }
    }, [handleAuthFailure]);

    /**
     * Au démarrage : si un token traîne dans `localStorage`, on vérifie
     * immédiatement qu'il est encore valide sur cette clé de site.
     *
     * C'est le moment où l'on rattrape le cas classique du passage de test à
     * production : un token de test dans le navigateur, une clé live dans le
     * bundle, et l'API répond `403 environment_mismatch`. La session est alors
     * purgée sans que le visiteur voie la moindre erreur.
     */
    const bootstrapStarted = useRef(false);

    useEffect(() => {
        // Garde contre le double montage de `StrictMode` en développement :
        // sans elle, `GET /me` partirait deux fois au chargement.
        //
        // Cette garde remplace le drapeau « requête annulée » qu'on met
        // habituellement dans le nettoyage d'un effet. Les deux ensemble
        // seraient un piège : le démontage simulé par `StrictMode` lèverait
        // le drapeau, et la réponse du premier appel — le seul émis, puisque
        // la garde bloque le second — serait alors ignorée. La session
        // resterait vide alors que l'API a bien répondu.
        //
        // Ce fournisseur vit aussi longtemps que l'application : il n'est
        // jamais réellement démonté, et il n'y a donc rien à annuler.
        if (bootstrapStarted.current) {
            return;
        }

        bootstrapStarted.current = true;

        async function bootstrap(): Promise<void> {
            if (!readStoredToken()) {
                setReady(true);

                return;
            }

            try {
                const me = await dashlite.me();

                setUser(me);
                setEntitlement(me.entitlement);
            } catch (error) {
                // `handleAuthFailure` purge la session si le token est refusé.
                // Sur une panne réseau ou une erreur serveur, on conserve le
                // token : il n'est pas en cause, le visiteur retentera.
                handleAuthFailure(error);
            } finally {
                setReady(true);
            }
        }

        void bootstrap();
    }, [handleAuthFailure]);

    const adoptAuthResult = useCallback((auth: AuthResult) => {
        applyToken(auth.token);
        // `AuthResult.user` a exactement la forme de `Me` : le claim d'un
        // paiement rapporte donc déjà le profil ET le droit d'accès — pas
        // besoin d'un `GET /me` supplémentaire au retour du tunnel.
        setUser(auth.user);
        setEntitlement(auth.user.entitlement);
    }, []);

    const login = useCallback(
        async (payload: { email: string; password: string }) => {
            const auth = await dashlite.login(payload);
            adoptAuthResult(auth);
        },
        [adoptAuthResult],
    );

    const logout = useCallback(async () => {
        try {
            await dashlite.logout();
        } catch {
            // Un token déjà révoqué côté serveur fait échouer l'appel : ce
            // n'est pas une raison pour laisser le visiteur « connecté ».
        } finally {
            clearSession();
        }
    }, [clearSession]);

    const deleteAccount = useCallback(async () => {
        await dashlite.deleteMe();
        clearSession();
    }, [clearSession]);

    const value = useMemo<SessionContextValue>(
        () => ({
            user,
            entitlement,
            ready,
            isAuthenticated: user !== null,
            hasAccess: entitlement?.has_access === true,
            login,
            logout,
            adoptAuthResult,
            refreshUser,
            refreshEntitlement,
            deleteAccount,
        }),
        [user, entitlement, ready, login, logout, adoptAuthResult, refreshUser, refreshEntitlement, deleteAccount],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
    const context = useContext(SessionContext);

    if (!context) {
        throw new Error('useSession doit être utilisé à l’intérieur de <SessionProvider>.');
    }

    return context;
}
